import { PERMISSION_KEYS, type PermissionKey } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Save, Search, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";

// ─── Permission catalog ─────────────────────────────────────────────────────
// Columns rendered left-to-right after the sticky name column. The Admin
// pseudo-column is rendered first and writes to users.is_super_admin instead
// of user_permissions. Granular keys follow PERMISSION_KEYS order.

type ColumnGroup = "admin" | "people" | "money" | "recruiting" | "labs";

interface PermissionMeta {
  key: PermissionKey | "__admin__";
  label: string;
  description: string;
  group: ColumnGroup;
}

// Full labels — the table scrolls horizontally on narrow viewports so the
// header doesn't need to abbreviate. The sticky Colaborador column stays
// visible while the user scrolls through permissions.
const COLUMNS: PermissionMeta[] = [
  {
    key: "__admin__",
    label: "Admin",
    description: "Acesso total à plataforma. Quando ligado, libera todas as outras colunas.",
    group: "admin",
  },
  {
    key: "employees",
    label: "Colaboradores",
    description: "Ver e editar a base de funcionários, incluindo salários e dados pessoais.",
    group: "people",
  },
  {
    key: "employees_manager",
    label: "Operador de Colaboradores",
    description:
      "Ver a base e atualizar status, líder, cargo, departamento, disciplina e nível, e sincronizar com o Slack. Sem salários nem dados pessoais.",
    group: "people",
  },
  {
    key: "org_chart",
    label: "Organograma",
    description: "Ver o organograma da empresa.",
    group: "people",
  },
  {
    key: "onboarding",
    label: "Onboarding",
    description: "Gerenciar templates e onboardings em andamento.",
    group: "people",
  },
  {
    key: "buddy_pool",
    label: "Pool de Buddies",
    description: "Adicionar e remover colaboradores do pool de buddies e ajustar o limite.",
    group: "people",
  },
  {
    key: "performance",
    label: "Desempenho",
    description: "Gerenciar ciclos de performance.",
    group: "people",
  },
  {
    key: "surveys",
    label: "Admin de Pesquisas",
    description: "Gerenciar pesquisas de clima e ver as respostas dos colaboradores.",
    group: "people",
  },
  {
    key: "surveys_manager",
    label: "Operador de Pesquisas",
    description:
      "Criar, disparar e acompanhar quem respondeu — sem acesso ao conteúdo das respostas.",
    group: "people",
  },
  {
    key: "salary_tables",
    label: "Tabelas Salariais",
    description: "Ver e editar tabelas salariais.",
    group: "money",
  },
  {
    key: "recruiting",
    label: "Vagas e Ofertas",
    description: "Recrutamento: vagas, ofertas e templates de oferta.",
    group: "recruiting",
  },
  {
    key: "approve_jobs_offers",
    label: "Aprovar",
    description: "Pode aprovar ou recusar vagas e ofertas via Slack.",
    group: "recruiting",
  },
  {
    key: "labs_intel",
    label: "Intel. Candidatos",
    description: "Inteligência de Candidatos (Labs).",
    group: "labs",
  },
  {
    key: "labs_feedback",
    label: "Feedback Loop",
    description: "Feedback Loop de Ofertas (Labs).",
    group: "labs",
  },
];

// First column of each group gets a left border to mark the cluster boundary.
// Walked once at module load so we don't recompute every render.
const GROUP_STARTS: ReadonlySet<PermissionMeta["key"]> = new Set(
  COLUMNS.filter((c, i) => i > 0 && c.group !== COLUMNS[i - 1].group).map((c) => c.key),
);

// ─── Row state ──────────────────────────────────────────────────────────────

interface MatrixUser {
  userId: string;
  email: string;
  fullName: string | null;
  jobTitle: string | null;
  isSuperAdmin: boolean;
  isManager: boolean;
  permissions: PermissionKey[];
}

interface RowDraft {
  isAdmin: boolean;
  permissions: Set<PermissionKey>;
}

function buildDraftMap(users: MatrixUser[]): Map<string, RowDraft> {
  const map = new Map<string, RowDraft>();
  for (const u of users) {
    map.set(u.userId, {
      isAdmin: u.isSuperAdmin,
      permissions: new Set(u.permissions),
    });
  }
  return map;
}

function rowsEqual(a: RowDraft, b: RowDraft): boolean {
  if (a.isAdmin !== b.isAdmin) return false;
  if (a.permissions.size !== b.permissions.size) return false;
  for (const p of Array.from(a.permissions)) {
    if (!b.permissions.has(p)) return false;
  }
  return true;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AccessControlPage({ embedded }: { embedded?: boolean }) {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState<Map<string, RowDraft>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  const { data, isLoading } = useQuery({
    ...orpc.settings.listUserPermissions.queryOptions(),
    staleTime: 30_000,
  });

  // Seed drafts whenever the matrix is (re)fetched. We clone so subsequent
  // edits don't mutate the cached server snapshot.
  useEffect(() => {
    if (data?.users) {
      setDrafts(buildDraftMap(data.users as MatrixUser[]));
    }
  }, [data]);

  const baseline = useMemo(() => {
    return data?.users ? buildDraftMap(data.users as MatrixUser[]) : new Map();
  }, [data]);

  const dirtyUserIds = useMemo(() => {
    const dirty = new Set<string>();
    for (const [userId, draft] of Array.from(drafts.entries())) {
      const original = baseline.get(userId);
      if (!original || !rowsEqual(draft, original)) dirty.add(userId);
    }
    return dirty;
  }, [drafts, baseline]);

  const isDirty = dirtyUserIds.size > 0;

  const filteredUsers = useMemo(() => {
    if (!data?.users) return [] as MatrixUser[];
    const needle = search.trim().toLowerCase();
    const onlyLinked = (data.users as MatrixUser[]).filter((u) => u.fullName);
    if (!needle) return onlyLinked;
    return onlyLinked.filter(
      (u) =>
        u.email.toLowerCase().includes(needle) ||
        (u.fullName?.toLowerCase().includes(needle) ?? false),
    );
  }, [data, search]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!data) return;
      const originalAdmins = new Set(
        (data.users as MatrixUser[]).filter((u) => u.isSuperAdmin).map((u) => u.userId),
      );
      const newAdmins = new Set<string>();
      for (const [userId, draft] of Array.from(drafts.entries())) {
        if (draft.isAdmin) newAdmins.add(userId);
      }

      const adminsChanged =
        newAdmins.size !== originalAdmins.size ||
        Array.from(newAdmins).some((id) => !originalAdmins.has(id));

      if (adminsChanged) {
        if (newAdmins.size === 0) {
          throw new Error("Pelo menos um Admin precisa existir.");
        }
        await rpcClient.settings.setSuperAdmins({ userIds: Array.from(newAdmins) });
      }

      // Persist granular permissions for every dirty non-admin row. Rows that
      // became admin skip this call — admin bypasses the table at read time
      // and we want to preserve their previous explicit grants in case admin
      // is later revoked.
      for (const userId of Array.from(dirtyUserIds)) {
        const draft = drafts.get(userId);
        if (!draft) continue;
        if (draft.isAdmin) continue;
        await rpcClient.settings.saveUserPermissions({
          userId,
          permissions: Array.from(draft.permissions),
        });
      }
    },
    onSuccess: () => {
      toast({ title: "Acessos salvos." });
      queryClient.invalidateQueries({
        queryKey: orpc.settings.listUserPermissions.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.auth.me.queryOptions().queryKey,
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Não foi possível salvar.";
      toast({ title: message, variant: "destructive" });
    },
    onSettled: () => setIsSaving(false),
  });

  // ── Cell toggles ──────────────────────────────────────────────────────────

  function toggleAdmin(userId: string) {
    setDrafts((prev) => {
      const next = new Map(prev);
      const row = next.get(userId);
      if (!row) return prev;
      next.set(userId, { ...row, isAdmin: !row.isAdmin });
      return next;
    });
  }

  function togglePermission(userId: string, permission: PermissionKey) {
    setDrafts((prev) => {
      const next = new Map(prev);
      const row = next.get(userId);
      if (!row) return prev;
      const perms = new Set(row.permissions);
      if (perms.has(permission)) perms.delete(permission);
      else perms.add(permission);
      next.set(userId, { ...row, permissions: perms });
      return next;
    });
  }

  // ── Counters ──────────────────────────────────────────────────────────────

  // Total users surfaced in the table (employees with a linked account). The
  // matrix hides users without an employee record, so the count denominator
  // matches what the admin actually sees.
  const totalLinkedUsers = useMemo(() => {
    if (!data?.users) return 0;
    return (data.users as MatrixUser[]).filter((u) => u.fullName).length;
  }, [data]);

  const counts = useMemo(() => {
    const result: Record<string, number> = { __admin__: 0 };
    for (const k of PERMISSION_KEYS) result[k] = 0;
    for (const draft of Array.from(drafts.values())) {
      if (draft.isAdmin) {
        result.__admin__ += 1;
        for (const k of PERMISSION_KEYS) result[k] += 1; // admins count as having every perm
      } else {
        for (const p of Array.from(draft.permissions)) result[p] += 1;
      }
    }
    return result;
  }, [drafts]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={cn("flex flex-col gap-4", embedded ? "" : "p-6")}
      data-testid="page-access-control"
    >
      {!embedded && (
        <header className="flex flex-col gap-1">
          <h1 className="text-[1.5rem] font-semibold leading-tight tracking-[-0.015em]">
            Controle de Acessos
          </h1>
          <p className="text-sm text-muted-foreground">
            Defina quem acessa cada funcionalidade. Admins veem tudo; managers preservam acesso a
            áreas do time. Permissões explícitas habilitam acesso pra quem não é admin nem manager.
          </p>
        </header>
      )}

      <Card>
        <CardContent className="p-0">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome ou email…"
                className="h-9 pl-9"
                data-testid="input-access-search"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground tabular-nums">
                {dirtyUserIds.size > 0
                  ? `${dirtyUserIds.size} alteraç${dirtyUserIds.size === 1 ? "ão" : "ões"} pendente${dirtyUserIds.size === 1 ? "" : "s"}`
                  : "Sem alterações pendentes"}
              </span>
              <Button
                onClick={() => {
                  setIsSaving(true);
                  saveMutation.mutate();
                }}
                disabled={!isDirty || isSaving}
                data-testid="button-save-access"
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Salvar
              </Button>
            </div>
          </div>

          {/* Matrix */}
          <div className="relative overflow-auto">
            <TooltipProvider delayDuration={150}>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b">
                    <th
                      className={cn(
                        "sticky left-0 z-10 bg-card text-left",
                        "h-11 min-w-[260px] px-4 align-middle",
                        "text-[0.75rem] font-semibold uppercase tracking-[0.01em] text-muted-foreground",
                      )}
                    >
                      Colaborador
                    </th>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className={cn(
                          "h-11 whitespace-nowrap px-3 text-center align-middle",
                          "text-[0.75rem] font-semibold tracking-[0.01em] text-muted-foreground",
                          GROUP_STARTS.has(col.key) && "border-l",
                        )}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help leading-tight">{col.label}</span>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[240px]">
                            <div className="space-y-1">
                              <div className="text-xs text-muted-foreground">{col.description}</div>
                              <div className="pt-1 text-[0.6875rem] text-muted-foreground tabular-nums">
                                {counts[col.key] ?? 0} de {totalLinkedUsers}{" "}
                                {totalLinkedUsers === 1 ? "colaborador" : "colaboradores"}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={COLUMNS.length + 1}
                        className="py-12 text-center text-muted-foreground"
                      >
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                      </td>
                    </tr>
                  )}
                  {!isLoading && filteredUsers.length === 0 && (
                    <tr>
                      <td
                        colSpan={COLUMNS.length + 1}
                        className="py-12 text-center text-sm text-muted-foreground"
                      >
                        {search
                          ? `Nada encontrado para «${search}».`
                          : "Nenhum colaborador com conta de usuário."}
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    filteredUsers.map((user) => {
                      const draft = drafts.get(user.userId);
                      if (!draft) return null;
                      const isSelf = currentUser?.id === user.userId;
                      const isDirtyRow = dirtyUserIds.has(user.userId);

                      return (
                        <tr
                          key={user.userId}
                          className={cn(
                            "group border-b transition-colors last:border-b-0 hover-elevate",
                            isDirtyRow && "bg-primary/[0.03]",
                          )}
                          data-testid={`row-access-${user.userId}`}
                        >
                          <td
                            className={cn(
                              "sticky left-0 z-10 h-11 min-w-[260px] px-4 align-middle",
                              "bg-card group-hover:bg-card",
                              isDirtyRow &&
                                "before:pointer-events-none before:absolute before:inset-0 before:bg-primary/[0.03]",
                            )}
                          >
                            <div className="min-w-0">
                              <div className="truncate font-medium leading-tight text-foreground">
                                {user.fullName ?? user.email}
                              </div>
                              <div className="flex items-center gap-1.5 truncate text-xs leading-tight text-muted-foreground">
                                <span className="truncate">{user.email}</span>
                                {isSelf && (
                                  <span className="inline-flex shrink-0 items-center gap-0.5 text-[0.6875rem] uppercase tracking-wider text-muted-foreground/80">
                                    <ShieldCheck className="h-3 w-3" />
                                    Você
                                  </span>
                                )}
                                {!isSelf && user.isManager && !draft.isAdmin && (
                                  <span className="shrink-0 text-[0.6875rem] uppercase tracking-wider text-muted-foreground/80">
                                    · Manager
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {COLUMNS.map((col) => {
                            const groupBorder = GROUP_STARTS.has(col.key) ? "border-l" : "";
                            if (col.key === "__admin__") {
                              const disabled = isSelf; // self-protection
                              return (
                                <td
                                  key={col.key}
                                  className={cn("h-11 px-3 text-center align-middle", groupBorder)}
                                >
                                  <Checkbox
                                    checked={draft.isAdmin}
                                    disabled={disabled}
                                    onCheckedChange={() => toggleAdmin(user.userId)}
                                    data-testid={`checkbox-admin-${user.userId}`}
                                  />
                                </td>
                              );
                            }
                            const permission = col.key;
                            const checkedByAdmin = draft.isAdmin;
                            const checked = checkedByAdmin || draft.permissions.has(permission);
                            return (
                              <td
                                key={permission}
                                className={cn("h-11 px-3 text-center align-middle", groupBorder)}
                              >
                                <Checkbox
                                  checked={checked}
                                  disabled={checkedByAdmin}
                                  onCheckedChange={() => togglePermission(user.userId, permission)}
                                  data-testid={`checkbox-${permission}-${user.userId}`}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
